// SkipCustomersListPage — /skip/customers.
//
// Thin wrapper over the unified `customers` table. Defaults to scoping
// to branch='skip' (legacy behaviour). Feature 1 adds a toggle that
// switches the server-side filter off, so the page can show every
// tenant customer for staff who need to add a skip job for someone
// who only exists on the scrap branch today. The "+ New Customer"
// action navigates to a small create form that auto-includes the
// 'skip' branch on submit.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { useSkipCustomers } from '../hooks/useSkipCustomers';
import type { CustomerSummary } from '../../../types/api';
import styles from './SkipCustomersListPage.module.css';

export function SkipCustomersListPage(): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  const filters: Record<string, string | number> = { page, limit: 25 };
  if (search) filters['search'] = search;
  if (showAll) filters['show_all'] = '1';

  const customers = useSkipCustomers(filters);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Skip Customers"
        subtitle="Customers with skip-hire jobs or enquiries"
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/skip/customers/create')}
          >
            New Customer
          </Button>
        }
      />

      <Card className={styles.filters}>
        <div className={styles.toggleRow} role="tablist" aria-label="Customer scope">
          <button
            type="button"
            role="tab"
            aria-selected={!showAll}
            className={[
              styles.toggleBtn,
              !showAll ? styles.toggleBtnActive : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              setShowAll(false);
              setPage(1);
            }}
          >
            Skip Customers
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={showAll}
            className={[
              styles.toggleBtn,
              showAll ? styles.toggleBtnActive : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              setShowAll(true);
              setPage(1);
            }}
          >
            All Customers
          </button>
        </div>
        <input
          type="search"
          placeholder="Search by name, email, phone or postcode…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className={styles.search}
          aria-label="Search customers"
        />
      </Card>

      {showAll ? (
        <p className={styles.banner} role="status">
          Showing all customers — not just skip customers.
        </p>
      ) : null}

      <Card>
        {customers.isLoading ? (
          <p className={styles.empty}>Loading…</p>
        ) : (customers.data?.customers.length ?? 0) === 0 ? (
          <p className={styles.empty}>No customers match your search.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Postcode</th>
                <th>Total Jobs</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {customers.data?.customers.map((c: CustomerSummary) => {
                const id = (c as CustomerSummary & { enc_id?: string }).enc_id ?? c.uuid;
                return (
                  <tr key={c.uuid}>
                    <td>
                      <Link to={`/skip/customers/${encodeURIComponent(id)}`}>{c.name}</Link>
                    </td>
                    <td>{c.email ?? '—'}</td>
                    <td>{c.phone}</td>
                    <td>{c.postcode ?? '—'}</td>
                    <td>{c.total_jobs}</td>
                    <td className={styles.actions}>
                      <Link to={`/skip/customers/${encodeURIComponent(id)}`}>View</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {customers.data && customers.data.pagination.totalPages > 1 ? (
          <div className={styles.pagination}>
            <Button
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className={styles.pageInfo}>
              Page {page} of {customers.data.pagination.totalPages} (
              {customers.data.pagination.total} total)
            </span>
            <Button
              variant="secondary"
              disabled={page >= customers.data.pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
